import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { postBusinessPrimary } from '~/api';
import Gap from '~/components/Gap';
import Header from '~/components/Header';
import MainContainer from '~/components/MainContainer';
import Text from '~/components/Text';
import UserProfile from '~/components/UserProfile';
import {
  ADD_BUSINESS_ROUTE,
  BILLING_ROUTE,
  BUSINESS_SETTINGS_ROUTE,
  EMAIL_LIST_ROUTE,
} from '~/constants/routes';
import AddIcon from '~/icons/AddIcon';
import BillingIcon from '~/icons/BillingIcon';
import EmailIcon from '~/icons/EmailIcon';
import LogoutIcon from '~/icons/LogoutIcon';
import SettingsIcon from '~/icons/SettingsIcon';
import TickIcon from '~/icons/TickIcon';
import UpgradeIcon from '~/icons/UpgradeIcon';
import { analytics, ANALYTICS_EVENTS } from '~/services/analyticsService';
import { hideLoader, showLoader } from '~/store/slices/app';
import { logOut, selectCompany } from '~/store/slices/user';
import { RootState } from '~/types';
import { IBusiness } from '~/types/business';
import { getScreenNameFromPath } from '~/utils/analytics';
import { apiResponseHandler } from '~/utils/api';

const AccountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 600px;
  margin: 0 auto;
`;

const Section = styled.div`
  background: white;
  border: 1px solid var(--border1);
  border-radius: 12px;
  padding: 16px;
`;

const CompanyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const CompanyItem = styled.div<{ isSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid var(--border1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${({ isSelected }) => (isSelected ? 'var(--neut7)' : 'transparent')};

  &:hover {
    background: var(--neut7);
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CompanyInitial = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f7f5ff;
  border: 1px solid var(--border1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--t-black);
  font-weight: 600;
  font-size: 16px;
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px;
  border: 1px solid var(--border1);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--neut3);
  text-decoration: none;
  margin-bottom: 12px;

  &:hover {
    background: var(--neut7);
    border-color: var(--prim4);
    color: var(--prim4);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const LogoutActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px;
  border: 1px solid var(--border1);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--neut3);
  margin-bottom: 12px;

  &:hover {
    background: var(--neut7);
    border-color: var(--error);
    color: var(--error);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmailSection = styled.div`
  text-align: center;
  padding: 10px 16px;
  background: var(--neut7);
  border-radius: 8px;
  margin-bottom: 0px;
`;

const EmailText = styled(Text)`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Account = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { email, currentPlan, companies, businessDetails } = useSelector(
    (state: RootState) => state.user,
  );

  const handleLogout = () => {
    const currentScreen = getScreenNameFromPath(location.pathname);
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: currentScreen,
      link_name: 'Log out',
      link_id: 'log_out_link',
      component_type: 'account_page',
    });
    dispatch(logOut());
  };

  async function handleCompanySelect(companyUuid: string) {
    dispatch(showLoader());
    const promise = await postBusinessPrimary(companyUuid);
    await apiResponseHandler<IBusiness>(promise);
    dispatch(hideLoader());
    dispatch(selectCompany(companyUuid));
    window.location.reload();
  }

  const getCompanyInitial = (companyName: string) => {
    return companyName.charAt(0).toUpperCase();
  };

  const handleUpgradePlanClick = () => {
    const currentScreen = getScreenNameFromPath(location.pathname);
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: currentScreen,
      link_name: 'Upgrade Plan',
      link_id: 'upgrade_link',
      component_type: 'account_page',
    });
  };

  const handlePlansAndBillingClick = () => {
    const currentScreen = getScreenNameFromPath(location.pathname);
    analytics.capture(ANALYTICS_EVENTS.LINK_CLICKED, {
      screen_name: currentScreen,
      link_name: 'Plans & Billing',
      link_id: 'plans_and_billing_link',
      component_type: 'account_page',
    });
  };

  return (
    <div>
      <Header headerText="Account" />
      <MainContainer>
        <AccountWrapper>
          <UserProfile isActionEnabled={false} />

          <EmailSection>
            <Text size="xs" variant="neutral" className="semi-bold">
              Logged in as
            </Text>
            <Gap t={4} />
            <EmailText size="xs">{email}</EmailText>
          </EmailSection>

          <Section>
            <Text size="xs" variant="neutral" className="semi-bold">
              Switch Business
            </Text>
            <Gap t={8} />

            <CompanyList>
              {companies.map(company => {
                const isSelected = company.business_uuid === businessDetails.business_uuid;
                return (
                  <CompanyItem
                    key={company.business_uuid}
                    isSelected={isSelected}
                    onClick={() => handleCompanySelect(company.business_uuid)}
                  >
                    <CompanyInfo>
                      <CompanyInitial>{getCompanyInitial(company.company_name)}</CompanyInitial>
                      <Text size="sm">{company.company_name}</Text>
                    </CompanyInfo>
                    {isSelected && <TickIcon stroke="var(--prim4)" />}
                  </CompanyItem>
                );
              })}
            </CompanyList>

            <ActionButton
              to={currentPlan === 'free' ? `${BILLING_ROUTE}?ref=businesses` : ADD_BUSINESS_ROUTE}
            >
              {currentPlan === 'free' ? <UpgradeIcon /> : <AddIcon />}
              <Text size="sm">Add Business</Text>
            </ActionButton>
          </Section>

          <Section>
            <Text size="xs" variant="neutral" className="semi-bold">
              Account Actions
            </Text>
            <Gap t={8} />

            <ActionButton to={BUSINESS_SETTINGS_ROUTE}>
              <SettingsIcon />
              <Text size="sm">Business Settings</Text>
            </ActionButton>

            {currentPlan !== 'infinity' && (
              <ActionButton to={BILLING_ROUTE} onClick={handleUpgradePlanClick}>
                <UpgradeIcon />
                <Text size="sm">Upgrade Plan</Text>
              </ActionButton>
            )}

            <ActionButton to={BILLING_ROUTE} onClick={handlePlansAndBillingClick}>
              <BillingIcon />
              <Text size="sm">Plans & Billing</Text>
            </ActionButton>

            <ActionButton to={EMAIL_LIST_ROUTE}>
              <EmailIcon fill="#313036" strokeWidth={1.25} />
              <Text size="sm">Emails</Text>
            </ActionButton>

            <LogoutActionButton onClick={handleLogout}>
              <LogoutIcon variant="danger" />
              <Text size="sm" variant="danger">
                Log out
              </Text>
            </LogoutActionButton>
          </Section>
        </AccountWrapper>
      </MainContainer>
    </div>
  );
};

export default Account;
